<template>
  <v-container fluid class="dashboard-container">
    <!-- Encabezado -->
    <div class="d-flex align-center flex-wrap mb-4">
      <div>
        <div class="page-title">Dashboard</div>
        <div class="page-subtitle">Resumen general de tu negocio</div>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="primary" depressed rounded :loading="loading" @click="loadSummary">
        <v-icon left small>mdi-refresh</v-icon>
        Actualizar
      </v-btn>
    </div>

    <v-row v-if="loading">
      <v-col cols="12" sm="6" md="3" v-for="i in 4" :key="`sk-kpi-${i}`">
        <v-skeleton-loader type="card" height="120"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="8">
        <v-skeleton-loader type="image" height="320"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="4">
        <v-skeleton-loader type="image" height="320"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="7">
        <v-skeleton-loader type="table"></v-skeleton-loader>
      </v-col>
      <v-col cols="12" md="5">
        <v-skeleton-loader type="list-item-three-line"></v-skeleton-loader>
      </v-col>
    </v-row>

    <template v-else>
      <!-- KPIs -->
      <v-row dense>
        <v-col cols="12" sm="6" md="3" v-for="kpi in kpiCards" :key="kpi.title">
          <v-card class="kpi-card" :style="{ borderTopColor: kpi.hex }">
            <v-card-text class="d-flex align-center">
              <v-avatar :color="kpi.color" size="52" class="kpi-avatar mr-4">
                <v-icon color="white" size="28">{{ kpi.icon }}</v-icon>
              </v-avatar>
              <div class="flex-grow-1">
                <div class="kpi-label">{{ kpi.title }}</div>
                <div class="kpi-value">{{ kpi.value }}</div>
                <div class="kpi-caption">{{ kpi.caption }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Gráficas -->
      <v-row dense class="mt-1">
        <v-col cols="12" md="8">
          <v-card>
            <v-card-title class="card-title-accent">
              Ventas de los últimos 14 días
              <v-spacer></v-spacer>
              <v-chip small color="accent" text-color="white" label>
                <v-icon left small>mdi-trending-up</v-icon>
                ${{ money(salesByDayTotal) }}
              </v-chip>
            </v-card-title>
            <v-card-text>
              <apex-chart
                type="area"
                height="290"
                :options="salesChartOptions"
                :series="salesChartSeries"
              ></apex-chart>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="4">
          <v-card class="fill-height">
            <v-card-title class="card-title-accent">Métodos de pago (30 días)</v-card-title>
            <v-card-text class="d-flex align-center justify-center">
              <apex-chart
                v-if="paymentChartSeries.length"
                type="donut"
                height="290"
                :options="paymentChartOptions"
                :series="paymentChartSeries"
              ></apex-chart>
              <div v-else class="text-center grey--text py-10">
                <v-icon large color="grey lighten-1">mdi-chart-donut</v-icon>
                <div class="mt-2">Sin pagos registrados</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Ventas recientes + inventario -->
      <v-row dense class="mt-1">
        <v-col cols="12" md="7">
          <v-card class="primary-block">
            <v-card-title class="card-title-accent">
              Ventas recientes
              <v-spacer></v-spacer>
              <v-btn small text color="primary" to="/admin/sales">
                Ver todas
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card-title>
            <v-data-table
              :headers="recentSalesHeaders"
              :items="summary.recentSales"
              dense
              fixed-header
              height="340"
              disable-pagination
              hide-default-footer
              no-data-text="Sin ventas recientes"
            >
              <template #[`item.ticketNumber`]="{ item }">
                <v-chip x-small label color="blue lighten-5" class="primary--text font-weight-bold">
                  #{{ item.ticketNumber }}
                </v-chip>
              </template>
              <template #[`item.productsCount`]="{ item }">
                {{ Number(item.productsCount || 0).toFixed(2) }}
              </template>
              <template #[`item.dateNew`]="{ item }">
                {{ date(item.dateNew) }}
              </template>
              <template #[`item.total`]="{ item }">
                <span class="font-weight-bold success--text">${{ money(item.total) }}</span>
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn icon small color="primary" @click="openTicketDetail(item)">
                  <v-icon small>mdi-eye-outline</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-col>

        <v-col cols="12" md="5">
          <v-card class="primary-block">
            <v-card-title class="card-title-accent">Estado del inventario</v-card-title>
            <v-list dense class="inventory-scroll">
              <div v-for="(item, index) in summary.inventoryStatus" :key="index" class="px-4 py-2">
                <div class="d-flex justify-space-between align-center mb-1">
                  <span class="font-weight-medium text-truncate mr-2">{{ item.productName }}</span>
                  <v-chip :color="stockMeta(item.totalUnits).color" x-small label dark>
                    {{ stockMeta(item.totalUnits).text }}
                  </v-chip>
                </div>
                <v-progress-linear
                  :value="stockMeta(item.totalUnits).progress"
                  :color="stockMeta(item.totalUnits).color"
                  height="8"
                  rounded
                ></v-progress-linear>
                <div class="text-caption grey--text mt-1">
                  {{ item.totalUnits }} unidades
                </div>
              </div>
              <div v-if="!summary.inventoryStatus.length" class="text-center grey--text py-8">
                Sin productos registrados
              </div>
            </v-list>
          </v-card>
        </v-col>
      </v-row>

      <!-- Top productos + morosos -->
      <v-row dense class="mt-1">
        <v-col cols="12" md="6">
          <v-card class="secondary-block">
            <v-card-title class="card-title-accent">Top productos (30 días)</v-card-title>
            <v-card-text v-if="topProductsChartSeries[0].data.length">
              <apex-chart
                type="bar"
                height="260"
                :options="topProductsChartOptions"
                :series="topProductsChartSeries"
              ></apex-chart>
            </v-card-text>
            <v-card-text v-else class="text-center grey--text py-10">
              <v-icon large color="grey lighten-1">mdi-chart-bar</v-icon>
              <div class="mt-2">Sin información de ventas</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card class="secondary-block">
            <v-card-title class="card-title-accent">
              Clientes morosos
              <v-spacer></v-spacer>
              <v-chip small color="warning" text-color="white" label>
                {{ summary.kpis.delinquentCustomers }} con deuda
              </v-chip>
            </v-card-title>
            <v-data-table
              :headers="delinquentHeaders"
              :items="summary.delinquentCustomers"
              dense
              fixed-header
              height="260"
              disable-pagination
              hide-default-footer
              no-data-text="Sin clientes con deuda activa"
            >
              <template #[`item.name`]="{ item }">
                <div class="d-flex align-center">
                  <v-avatar color="orange lighten-4" size="26" class="mr-2">
                    <span class="orange--text text--darken-3 text-caption font-weight-bold">
                      {{ initials(item.name) }}
                    </span>
                  </v-avatar>
                  {{ item.name }}
                </div>
              </template>
              <template #[`item.currentDebt`]="{ item }">
                <span class="font-weight-bold error--text">${{ money(item.currentDebt) }}</span>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Detalle de ticket -->
      <v-dialog v-model="detailDialog" max-width="900px">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Detalle de ticket {{ selectedTicketDetail.ticket?.ticketNumber || '-' }}</span>
            <v-btn icon @click="detailDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <v-row dense v-if="selectedTicketDetail.ticket">
              <v-col cols="12" md="4"><strong>Cliente:</strong> {{ selectedTicketDetail.ticket.customerName }}</v-col>
              <v-col cols="12" md="4"><strong>Productos:</strong> {{ Number(selectedTicketDetail.ticket.productsCount || 0).toFixed(2) }}</v-col>
              <v-col cols="12" md="4"><strong>Total:</strong> ${{ Number(selectedTicketDetail.ticket.total || 0).toFixed(2) }}</v-col>
            </v-row>
            <v-data-table
              :headers="detailHeaders"
              :items="selectedTicketDetail.lines"
              :loading="loadingDetail"
              dense
              disable-pagination
              hide-default-footer
              no-data-text="Sin líneas de ticket"
              class="mt-3"
            >
              <template #[`item.units`]="{ item }">
                {{ Number(item.units || 0).toFixed(2) }}
              </template>
              <template #[`item.price`]="{ item }">
                ${{ Number(item.price || 0).toFixed(2) }}
              </template>
              <template #[`item.subtotal`]="{ item }">
                ${{ Number(item.subtotal || 0).toFixed(2) }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-dialog>
    </template>
  </v-container>
</template>

<script>
import * as dashboardService from '@/services/dashboardService';
import * as salesService from '@/services/salesService';
import { formatDateTimeByTimezone } from '@/utils/dateTime';
import { transactionPaymentLabel } from '@/utils/legacyPosLabels';

export default {
  name: 'AdminDashboardView',
  data() {
    return {
      timezoneVersion: 0,
      loading: false,
      summary: {
        kpis: {
          salesToday: 0,
          salesLast30Days: 0,
          ticketsToday: 0,
          delinquentCustomers: 0,
          lowStockProducts: 0,
        },
        recentSales: [],
        topProducts: [],
        inventoryStatus: [],
        delinquentCustomers: [],
        salesByDay: [],
        paymentsByMethod: [],
      },
      recentSalesHeaders: [
        { text: 'Folio', value: 'ticketNumber', sortable: false },
        { text: 'Cliente', value: 'customerName', sortable: false },
        { text: 'Productos', value: 'productsCount', sortable: false, align: 'end' },
        { text: 'Fecha', value: 'dateNew', sortable: false },
        { text: 'Total', value: 'total', sortable: false, align: 'end' },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ],
      delinquentHeaders: [
        { text: 'Código', value: 'searchKey', sortable: false },
        { text: 'Cliente', value: 'name', sortable: false },
        { text: 'Ciudad', value: 'city', sortable: false },
        { text: 'Deuda actual', value: 'currentDebt', sortable: false, align: 'end' },
      ],
      detailDialog: false,
      loadingDetail: false,
      selectedTicketDetail: {
        ticket: null,
        lines: [],
      },
      detailHeaders: [
        { text: '#', value: 'line', sortable: false, width: '60px' },
        { text: 'Producto', value: 'productName', sortable: false },
        { text: 'Cantidad', value: 'units', sortable: false, align: 'end' },
        { text: 'Precio', value: 'price', sortable: false, align: 'end' },
        { text: 'Subtotal', value: 'subtotal', sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    kpiCards() {
      const k = this.summary.kpis;
      return [
        {
          title: 'Ventas hoy',
          value: `$${this.money(k.salesToday)}`,
          caption: `Últimos 30 días: $${this.money(k.salesLast30Days)}`,
          icon: 'mdi-currency-usd',
          color: 'primary',
          hex: '#0066CC',
        },
        {
          title: 'Tickets hoy',
          value: String(k.ticketsToday),
          caption: 'Tickets emitidos hoy',
          icon: 'mdi-receipt-text-outline',
          color: 'accent',
          hex: '#00BCD4',
        },
        {
          title: 'Clientes morosos',
          value: String(k.delinquentCustomers),
          caption: 'Clientes con deuda activa',
          icon: 'mdi-account-alert-outline',
          color: 'warning',
          hex: '#FFA726',
        },
        {
          title: 'Bajo stock',
          value: String(k.lowStockProducts),
          caption: 'Con existencias ≤ 5 unidades',
          icon: 'mdi-alert-circle-outline',
          color: 'error',
          hex: '#EF5350',
        },
      ];
    },
    salesByDayTotal() {
      return (this.summary.salesByDay || []).reduce((acc, d) => acc + Number(d.total || 0), 0);
    },
    // Serie continua de 15 días: rellena con cero los días sin ventas
    // para que la gráfica no "salte" fechas.
    salesByDayFilled() {
      const byDay = {};
      (this.summary.salesByDay || []).forEach((d) => {
        byDay[String(d.day).slice(0, 10)] = d;
      });
      const days = [];
      for (let i = 14; i >= 0; i -= 1) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const key = date.toISOString().slice(0, 10);
        days.push(byDay[key] || { day: key, total: 0, tickets: 0 });
      }
      return days;
    },
    salesChartSeries() {
      return [
        { name: 'Ventas', data: this.salesByDayFilled.map((d) => Number(d.total || 0).toFixed(2)) },
      ];
    },
    salesChartOptions() {
      const days = this.salesByDayFilled;
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false }, fontFamily: 'inherit' },
        colors: ['#0066CC'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            colorStops: [
              { offset: 0, color: '#00BCD4', opacity: 0.5 },
              { offset: 100, color: '#0066CC', opacity: 0.05 },
            ],
          },
        },
        stroke: { curve: 'smooth', width: 3, colors: ['#0066CC'] },
        dataLabels: { enabled: false },
        xaxis: {
          categories: days.map((d) => this.shortDay(d.day)),
          labels: { style: { colors: '#6b8299' } },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: '#6b8299' },
            formatter: (val) => `$${Number(val).toFixed(0)}`,
          },
        },
        grid: { borderColor: '#edf2f7', strokeDashArray: 4 },
        tooltip: {
          y: { formatter: (val) => `$${Number(val).toFixed(2)}` },
        },
        markers: { size: 4, colors: ['#fff'], strokeColors: '#0066CC', strokeWidth: 2 },
      };
    },
    paymentChartSeries() {
      return (this.summary.paymentsByMethod || []).map((p) => Number(p.total || 0));
    },
    paymentChartOptions() {
      const methods = this.summary.paymentsByMethod || [];
      return {
        labels: methods.map((p) => transactionPaymentLabel(p.method)),
        colors: ['#0066CC', '#00BCD4', '#2ECA71', '#FFA726', '#AB47BC', '#EF5350', '#29B6F6', '#8D6E63'],
        legend: { position: 'bottom', fontFamily: 'inherit' },
        dataLabels: {
          formatter: (val) => `${Number(val).toFixed(1)}%`,
        },
        tooltip: {
          y: { formatter: (val) => `$${Number(val).toFixed(2)}` },
        },
        plotOptions: {
          pie: {
            donut: {
              size: '68%',
              labels: {
                show: true,
                total: {
                  show: true,
                  label: 'Total',
                  formatter: (w) => `$${w.globals.seriesTotals.reduce((a, b) => a + b, 0).toFixed(2)}`,
                },
              },
            },
          },
        },
        stroke: { width: 0 },
      };
    },
    topProductsChartSeries() {
      const products = this.summary.topProducts || [];
      return [
        { name: 'Unidades', data: products.map((p) => Number(p.unitsSold || 0).toFixed(2)) },
      ];
    },
    topProductsChartOptions() {
      const products = this.summary.topProducts || [];
      return {
        chart: { toolbar: { show: false }, fontFamily: 'inherit' },
        colors: ['#00BCD4'],
        plotOptions: {
          bar: { horizontal: true, borderRadius: 6, barHeight: '55%', distributed: false },
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: products.map((p) => this.truncate(p.productName, 26)),
          labels: { style: { colors: '#6b8299' } },
        },
        yaxis: { labels: { style: { colors: '#14395e' } } },
        grid: { borderColor: '#edf2f7', strokeDashArray: 4 },
        tooltip: {
          y: {
            formatter: (val, { dataPointIndex }) => {
              const revenue = Number(products[dataPointIndex]?.revenue || 0).toFixed(2);
              return `${Number(val).toFixed(2)} unidades · $${revenue}`;
            },
          },
        },
      };
    },
  },
  async mounted() {
    window.addEventListener('app-timezone-changed', this.handleTimezoneChanged);
    await this.loadSummary();
  },
  beforeDestroy() {
    window.removeEventListener('app-timezone-changed', this.handleTimezoneChanged);
  },
  methods: {
    handleTimezoneChanged() {
      this.timezoneVersion += 1;
      this.loadSummary();
    },
    async loadSummary() {
      this.loading = true;
      try {
        const data = await dashboardService.fetchDashboardSummary();
        this.summary = {
          salesByDay: [],
          paymentsByMethod: [],
          ...data,
        };
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar el dashboard.', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    money(value) {
      return Number(value || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    date(value) {
      return formatDateTimeByTimezone(value, '-');
    },
    shortDay(value) {
      if (!value) return '';
      const raw = String(value).slice(0, 10);
      const [, month, day] = raw.split('-');
      return `${day}/${month}`;
    },
    truncate(text, max) {
      const value = String(text || '');
      return value.length > max ? `${value.slice(0, max - 1)}…` : value;
    },
    initials(name) {
      return String(name || '?')
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0).toUpperCase())
        .join('');
    },
    stockMeta(units) {
      const n = Number(units || 0);
      if (n <= 0) return { text: 'Agotado', color: 'error', progress: 100 };
      if (n <= 5) return { text: 'Bajo', color: 'warning', progress: Math.min((n / 20) * 100, 100) };
      return { text: 'En stock', color: 'success', progress: Math.min((n / 50) * 100, 100) };
    },
    async openTicketDetail(ticket) {
      this.detailDialog = true;
      this.loadingDetail = true;
      this.selectedTicketDetail = { ticket: null, lines: [] };
      try {
        this.selectedTicketDetail = await salesService.fetchSalesTicketDetail(ticket.receiptId);
      } catch (error) {
        this.$emit('show-alert', { message: 'Error al cargar detalle del ticket.', type: 'error' });
      } finally {
        this.loadingDetail = false;
      }
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  max-width: 1500px;
}

.kpi-card {
  border-top: 3px solid transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
}

.kpi-avatar {
  box-shadow: 0 4px 10px rgba(3, 49, 86, 0.18);
}

.kpi-label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b8299;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #14395e;
  line-height: 1.3;
}

.kpi-caption {
  font-size: 0.72rem;
  color: #90a4b8;
}

.primary-block {
  min-height: 420px;
}

.secondary-block {
  min-height: 340px;
}

.inventory-scroll {
  max-height: 360px;
  overflow-y: auto;
}
</style>
